import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, AlertCircle, Eye, EyeOff, Check, Camera, Upload } from 'lucide-react';

const PLANS = {
  brand_growth: { name: "Brand Growth", user_type: "brand", price: 99 },
  brand_starter: { name: "Brand Starter", user_type: "brand", price: 199 },
  brand_enterprise: { name: "Brand Enterprise", user_type: "brand", price: 399 },
  influencer_starter: { name: "Influencer Starter", user_type: "influencer", price: 19 },
  influencer_pro: { name: "Influencer Pro", user_type: "influencer", price: 79.99 },
  personal: { name: "Personal", user_type: "personal", price: 9 }
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isAuthenticated } = useAuth();
  
  const planId = searchParams.get('plan');
  const userType = searchParams.get('type');
  const promoCode = searchParams.get('promo');
  const plan = PLANS[planId];

  // Persist promo code to localStorage so it survives redirects
  useEffect(() => {
    if (promoCode) {
      localStorage.setItem('vf_promo_code', promoCode);
    }
  }, [promoCode]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    handle: '',
    company_name: '',
    bio: '',
    website: '',
    age_confirmed: false,
    biometric_consent: false,
    profile_image: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [handleAvailable, setHandleAvailable] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!planId || !plan) {
      navigate('/subscribe');
    }
  }, [planId, plan, navigate]);

  const checkHandle = async (handle) => {
    if (handle.length < 3) {
      setHandleAvailable(null);
      return;
    }
    // Simple client-side validation for now
    setHandleAvailable(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    if (name === 'handle') {
      checkHandle(value);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setProfilePreview(base64);
      setFormData(prev => ({ ...prev, profile_image: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.age_confirmed) {
      setError('You must confirm that you are at least 18 years old to use VirtualFit');
      return;
    }

    if (!formData.biometric_consent) {
      setError('You must consent to biometric data processing to create an account');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (formData.handle.length < 3) {
      setError('Handle must be at least 3 characters');
      return;
    }

    setLoading(true);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        handle: formData.handle.toLowerCase().replace(/[^a-z0-9]/g, ''),
        user_type: userType,
        plan_id: planId,
        // Consent: biometric_consent block links to Privacy Policy + Biometric Data Policy
        // and the user has explicitly checked it, so privacy_consent is implicitly true.
        privacy_consent: formData.biometric_consent === true,
        age_confirmed: formData.age_confirmed === true,
        biometric_consent: formData.biometric_consent === true,
        company_name: formData.company_name || null,
        bio: formData.bio || null,
        website: formData.website || null,
        profile_image: formData.profile_image || null
      });

      // Meta Pixel — CompleteRegistration (fires only after backend confirms
      // account creation succeeded). Guarded by fbq presence; fbq only exists
      // after the visitor accepts analytics cookies via vf_cookie_consent.
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'CompleteRegistration');
      }

      // Redirect to checkout
      const promoQuery = promoCode ? `&promo=${promoCode}` : '';
      navigate('/checkout?plan=' + planId + promoQuery);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!plan) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">
            VirtualFit
          </Link>
          <Link to="/login" className="text-gray-600 hover:text-rose-500 font-medium">
            Already have an account? Sign in
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Plan Summary */}
          <div className="bg-gradient-to-r from-rose-50 to-orange-50 rounded-xl p-4 mb-8">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Selected Plan</p>
                <p className="font-bold text-gray-800">{plan.name}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-800">${plan.price}</p>
                <p className="text-sm text-gray-500">/month</p>
              </div>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-800 mb-6">Create Your Account</h1>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Image Upload (for brands and influencers) */}
            {(userType === 'brand' || userType === 'influencer') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {userType === 'brand' ? 'Brand Logo (Optional)' : 'Profile Picture (Optional)'}
                </label>
                <div className="flex items-center space-x-4">
                  <div className={`w-20 h-20 rounded-xl overflow-hidden border-2 border-dashed ${profilePreview ? 'border-transparent' : 'border-gray-300'} flex items-center justify-center bg-gray-50`}>
                    {profilePreview ? (
                      <img src={profilePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-8 h-8 text-gray-300" />
                    )}
                  </div>
                  <div>
                    <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                      <Upload className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="text-sm text-gray-700">Upload Image</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB</p>
                  </div>
                </div>
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {userType === 'brand' ? 'Your Name' : 'Full Name'}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                placeholder="John Doe"
              />
            </div>

            {/* Company Name (for brands) */}
            {userType === 'brand' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="Fashion Brand Inc."
                />
              </div>
            )}

            {/* Handle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Handle (your unique @username)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                <input
                  type="text"
                  name="handle"
                  value={formData.handle}
                  onChange={handleChange}
                  required
                  className="w-full pl-8 pr-10 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="yourhandle"
                />
                {handleAvailable !== null && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2">
                    {handleAvailable ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">This will be your public profile URL</p>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>

            {/* Bio (optional for brands/influencers) */}
            {userType !== 'personal' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bio (optional)</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="Tell us about yourself or your brand..."
                />
              </div>
            )}

            {/* Combined Privacy Notice + Biometric Consent + Age Confirmation */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start">
                <Shield className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-amber-800 mb-2">Privacy Notice</p>
                  <p className="text-sm text-amber-700 mb-3">
                    By creating an account, you acknowledge that:
                  </p>
                  <ul className="text-sm text-amber-700 space-y-1 mb-4">
                    <li>• Your uploaded images will be processed by third-party AI services (Google Gemini)</li>
                    <li>• Generated images are stored for your use and may be cached to improve service</li>
                    <li>• We do not sell your personal data or images to third parties</li>
                    <li>• You can delete your account and all associated data at any time</li>
                  </ul>

                  {/* Biometric Consent — BIPA / GDPR explicit consent */}
                  <label className="flex items-start cursor-pointer mb-3" data-testid="biometric-consent-label">
                    <input
                      type="checkbox"
                      name="biometric_consent"
                      checked={formData.biometric_consent}
                      onChange={handleChange}
                      className="w-5 h-5 min-w-[20px] rounded border-amber-300 text-rose-500 focus:ring-rose-500 mr-3 mt-0.5 cursor-pointer"
                      data-testid="biometric-consent-checkbox"
                    />
                    <span className="text-sm text-amber-800 font-medium leading-relaxed">
                      I have read the{' '}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-rose-500 underline hover:text-orange-500"
                      >
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a
                        href="/biometric-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-rose-500 underline hover:text-orange-500"
                      >
                        Biometric Data Policy
                      </a>
                      . I explicitly consent for VirtualFit to process my biometric data (facial geometry
                      and body measurements) for the purpose of generating virtual try-on images.
                    </span>
                  </label>

                  {/* Age Confirmation (18+) — COPPA / BIPA compliance */}
                  <label className="flex items-start cursor-pointer" data-testid="age-confirm-label">
                    <input
                      type="checkbox"
                      name="age_confirmed"
                      checked={formData.age_confirmed}
                      onChange={handleChange}
                      className="w-5 h-5 min-w-[20px] rounded border-amber-300 text-rose-500 focus:ring-rose-500 mr-3 mt-0.5 cursor-pointer"
                      data-testid="age-confirm-checkbox"
                    />
                    <span className="text-sm text-amber-800 font-medium leading-relaxed">
                      I confirm that I am at least 18 years old and eligible to use VirtualFit.
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !formData.age_confirmed || !formData.biometric_consent}
              className="w-full py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="continue-to-payment-btn"
            >
              {loading ? 'Creating Account...' : 'Continue to Payment'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
